import { useCallback, useEffect, useState } from 'react'
import { usePublicClient, useChainId } from 'wagmi'
import { useActiveWeb3React } from '../../hooks'
import { classic } from '../../wagmi'
import useDebounce from '../../hooks/useDebounce'
import useIsWindowVisible from '../../hooks/useIsWindowVisible'
import { updateBlockNumber } from './actions'
import { useDispatch } from 'react-redux'

export default function Updater(): null {
  const { chainId } = useActiveWeb3React()
  const wagmiChainId = useChainId()
  // Get public client for current chain, default to ETC mainnet
  const effectiveChainId = wagmiChainId || chainId || classic.id
  const publicClient = usePublicClient({ chainId: effectiveChainId })
  const dispatch = useDispatch()

  const windowVisible = useIsWindowVisible()

  const [state, setState] = useState<{ chainId: number | undefined; blockNumber: number | null }>({
    chainId,
    blockNumber: null
  })

  const blockNumberCallback = useCallback(
    (blockNumber: bigint) => {
      // Convert bigint to number for compatibility with existing state
      const blockNum = Number(blockNumber)
      setState(state => {
        if (chainId === state.chainId) {
          if (typeof state.blockNumber !== 'number') return { chainId, blockNumber: blockNum }
          return { chainId, blockNumber: Math.max(blockNum, state.blockNumber) }
        }
        return state
      })
    },
    [chainId, setState]
  )

  // attach/detach listeners
  useEffect(() => {
    if (!publicClient || !chainId || !windowVisible) return undefined

    setState({ chainId, blockNumber: null })

    // Get initial block number
    publicClient
      .getBlockNumber()
      .then(blockNumberCallback)
      .catch((error: Error) => console.error(`Failed to get block number for chainId: ${chainId}`, error))

    // Watch for new blocks
    // ETC has ~14s block times, poll every 15s to avoid hammering public RPCs
    const unwatch = publicClient.watchBlockNumber({
      onBlockNumber: blockNumberCallback,
      poll: true,
      pollingInterval: 15_000,
    })

    return () => {
      unwatch()
    }
  }, [dispatch, chainId, publicClient, blockNumberCallback, windowVisible])

  const debouncedState = useDebounce(state, 100)

  useEffect(() => {
    if (!debouncedState.chainId || !debouncedState.blockNumber || !windowVisible) return
    dispatch(updateBlockNumber({ chainId: debouncedState.chainId, blockNumber: debouncedState.blockNumber }))
  }, [windowVisible, dispatch, debouncedState.blockNumber, debouncedState.chainId])

  return null
}
